import React, { FC, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Checkbox, Divider, Form, FormInstance, Input, Select } from 'antd';
import { DefaultOptionType } from 'antd/lib/select';
import { RuleObject } from 'antd/lib/form';
import { BillingDocumentType, CustomerType } from 'apollo/types';
import useListBillingSystemCitiesLazyQuery from 'apollo/resolvers/billingSystem/city/list';
import useListBillingSystemCommunesLazyQuery from 'apollo/resolvers/billingSystem/commune/list';
import LoadingModal from 'components/generalUI/LoadingModal';
import cellPhoneRegExp from 'utils/validations/cellPhoneRegExp';
import emailRegExp from 'utils/validations/emailRegExp';
import rutFormValidator from 'utils/validations/rutFormValidator';
import dniValidator from 'utils/validations/dniValidator';
import rutFormat from 'utils/formats/rutFormat';
import { tosLink } from 'assets/media/policies';
import {
  CONTACT_FORM_INITIAL_VALUES,
  ContactFormValues,
} from 'views/TicketSummary/ticketSummary.viewModel';
import styles from './ContactForm.module.scss';

interface ContactFormProps {
  form: FormInstance;
  onValuesChange: (values: ContactFormValues) => void;
  onIncompleteSubmit: () => void;
  onFinish: (values: ContactFormValues) => void;
}

const ContactForm: FC<ContactFormProps> = ({
  form,
  onValuesChange,
  onIncompleteSubmit,
  onFinish,
}: ContactFormProps) => {
  const { t } = useTranslation();

  const [isInvoice, setIsInvoice] = useState(false);
  const [isPassport, setIsPassport] = useState(false);
  const [customerType, setCustomerType] = useState<CustomerType>(CustomerType.Person);
  const [selectedCityId, setSelectedCityId] = useState<string>();
  const [cities, setCities] = useState<DefaultOptionType[]>();
  const [communes, setCommunes] = useState<DefaultOptionType[]>();

  const [listBillingSystemCities, { loading: citiesLoading }] =
    useListBillingSystemCitiesLazyQuery();
  const [listBillingSystemCommunes, { loading: communesLoading }] =
    useListBillingSystemCommunesLazyQuery();

  const isCompany = isInvoice && customerType === CustomerType.Company;

  const billingDocumentTypeOptions: DefaultOptionType[] = [
    { label: t('checkout.summary.voucher'), value: BillingDocumentType.Voucher },
    { label: t('checkout.summary.invoice'), value: BillingDocumentType.Invoice },
  ];

  const documentTypeOptions: DefaultOptionType[] = [
    { label: t('checkout.summary.rut'), value: false },
    { label: t('checkout.summary.passport'), value: true },
  ];

  const customerTypeOptions: DefaultOptionType[] = [
    { label: t('checkout.summary.person'), value: CustomerType.Person },
    { label: t('checkout.summary.company'), value: CustomerType.Company },
  ];

  const onBillingDocumentTypeChange = (value: BillingDocumentType) => {
    const invoiceSelected = value === BillingDocumentType.Invoice;
    setIsInvoice(invoiceSelected);
    if (invoiceSelected) {
      form.setFieldsValue({ documentType: false });
      setIsPassport(false);
    }
  };

  const listCities = async () => {
    const response = await listBillingSystemCities();
    if (response && response.data) {
      setCities(
        response.data.listBillingSystemCities.map((city) => ({
          label: city.name,
          value: city.id,
        }))
      );
    }
  };

  const listCommunes = async () => {
    if (!selectedCityId) return;
    const response = await listBillingSystemCommunes({
      variables: { input: { billingSystemCityId: selectedCityId } },
    });
    if (response && response.data) {
      setCommunes(
        response.data.listBillingSystemCommunes.map((commune) => ({
          label: commune.name,
          value: commune.id,
        }))
      );
      form.setFieldsValue({ customerCommuneId: undefined });
    }
  };

  const filterByValue = (value: string, option: DefaultOptionType | undefined) => {
    if (!option || !option.label) return false;
    return option.label.toString().toLowerCase().includes(value.toLowerCase());
  };

  const validateConfirmEmail = (rule: RuleObject, value: string) => {
    const formEmail = form.getFieldValue('email') as string;
    const isMailEqual = value && formEmail && value.trim() === formEmail.trim();
    if (!isMailEqual) {
      return Promise.reject(new Error(t('validation.emailsMustMatch')));
    }
    return Promise.resolve();
  };

  const validateAcceptance = (rule: RuleObject, value: boolean) => {
    if (!value) {
      return Promise.reject(new Error(t('checkout.summary.politicsWarning')));
    }
    return Promise.resolve();
  };

  const tosComponents = {
    tos: (
      <a
        className={styles.Href}
        href={tosLink}
        target="_blank"
        rel="noreferrer"
        aria-label={t('checkout.summary.TOS')}
      />
    ),
  };

  useEffect(() => {
    if (isInvoice) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      listCities();
    }
  }, [isInvoice]);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    listCommunes();
  }, [selectedCityId]);

  const renderDocumentNumberField = () => {
    if (isPassport) {
      return (
        <Form.Item
          name="documentNumber"
          rules={[
            { required: true, message: t('checkout.summary.warning') },
            { validator: dniValidator },
          ]}
        >
          <Input placeholder={`${t('checkout.summary.dni')} *`} />
        </Form.Item>
      );
    }
    return (
      <Form.Item
        name="documentNumber"
        rules={[
          { required: true, message: t('checkout.summary.warning') },
          { validator: rutFormValidator },
        ]}
        normalize={rutFormat}
      >
        <Input placeholder={`${t('checkout.summary.rutExample')} *`} />
      </Form.Item>
    );
  };

  const renderInvoiceSection = () => {
    if (!isInvoice) return <></>;
    return (
      <>
        <Divider className={`${styles.Divider} ${styles.FullWidth}`} />
        <h3 className={`${styles.SectionTitle} ${styles.FullWidth}`}>
          {t('pages.ticketSummary.form.invoiceSection')}
        </h3>

        <Form.Item
          name="customerType"
          rules={[{ required: true, message: t('checkout.summary.warning') }]}
        >
          <Select
            options={customerTypeOptions}
            placeholder={`${t('pages.ticketSummary.form.customerType')} *`}
            onChange={setCustomerType}
          />
        </Form.Item>
        <Form.Item
          name="customerBusinessActivity"
          rules={[{ required: true, message: t('checkout.summary.warning') }]}
        >
          <Input placeholder={`${t('checkout.summary.customerBusinessActivity')} *`} />
        </Form.Item>
        <Form.Item
          name="customerCityId"
          rules={[{ required: true, message: t('checkout.summary.warning') }]}
        >
          <Select
            showSearch
            filterOption={filterByValue}
            options={cities}
            placeholder={`${t('checkout.summary.city')} *`}
            onChange={setSelectedCityId}
          />
        </Form.Item>
        <Form.Item
          name="customerCommuneId"
          rules={[{ required: true, message: t('checkout.summary.warning') }]}
        >
          <Select
            showSearch
            filterOption={filterByValue}
            options={communes}
            placeholder={`${t('checkout.summary.commune')} *`}
          />
        </Form.Item>
        <Form.Item
          name="customerAddress"
          rules={[{ required: true, message: t('checkout.summary.warning') }]}
        >
          <Input placeholder={`${t('checkout.summary.address')} *`} />
        </Form.Item>
      </>
    );
  };

  return (
    <>
      <LoadingModal visible={citiesLoading || communesLoading} />

      <h2 className={styles.Title}>{t('checkout.summary.contactDetails')}</h2>

      <Form
        className={styles.Form}
        form={form}
        initialValues={CONTACT_FORM_INITIAL_VALUES}
        onValuesChange={(changed, allValues: ContactFormValues) =>
          onValuesChange(allValues)
        }
        onFinishFailed={onIncompleteSubmit}
        onFinish={onFinish}
      >
        <Form.Item
          name="billingDocumentType"
          rules={[{ required: true, message: t('checkout.summary.warning') }]}
        >
          <Select
            options={billingDocumentTypeOptions}
            placeholder={`${t('pages.ticketSummary.form.billingDocumentType')} *`}
            onChange={onBillingDocumentTypeChange}
          />
        </Form.Item>

        <Form.Item
          name="firstName"
          rules={[{ required: true, message: t('checkout.summary.warning') }]}
        >
          <Input
            placeholder={`${
              isCompany
                ? t('checkout.summary.companyName')
                : t('checkout.summary.firstName')
            } *`}
          />
        </Form.Item>

        {!isCompany && (
          <Form.Item
            name="lastName"
            rules={[{ required: true, message: t('checkout.summary.warning') }]}
          >
            <Input placeholder={`${t('checkout.summary.lastName')} *`} />
          </Form.Item>
        )}

        <Form.Item
          name="phone"
          rules={[
            { required: true, message: t('checkout.summary.warning') },
            {
              message: t('checkout.summary.invalidCellphone'),
              pattern: cellPhoneRegExp(),
            },
          ]}
        >
          <Input placeholder={`${t('checkout.summary.phone')} *, Ej: +56912345678`} />
        </Form.Item>

        <Form.Item
          name="email"
          rules={[
            { required: true, message: t('checkout.summary.warning') },
            { message: t('checkout.summary.invalidEmail'), pattern: emailRegExp() },
          ]}
        >
          <Input placeholder={`${t('checkout.summary.email')} *`} />
        </Form.Item>

        <Form.Item
          name="confirmEmail"
          rules={[
            { required: true, message: t('checkout.summary.warning') },
            { message: t('checkout.summary.invalidEmail'), pattern: emailRegExp() },
            { validator: validateConfirmEmail },
          ]}
        >
          <Input placeholder={`${t('checkout.summary.confirmEmail')} *`} />
        </Form.Item>

        <Form.Item name="documentType">
          <Select
            options={documentTypeOptions}
            disabled={isInvoice}
            placeholder={t('pages.ticketSummary.form.documentType')}
            onChange={setIsPassport}
          />
        </Form.Item>

        {renderDocumentNumberField()}

        {renderInvoiceSection()}

        <Form.Item
          className={styles.FullWidth}
          name="acceptParkPolitics"
          valuePropName="checked"
          rules={[{ validator: validateAcceptance }]}
        >
          <Checkbox className={styles.Checkbox}>
            <Trans i18nKey="checkout.summary.tosAcceptance" components={tosComponents} />
          </Checkbox>
        </Form.Item>

        <Form.Item
          className={styles.FullWidth}
          name="acceptValuablesPolicy"
          valuePropName="checked"
          rules={[{ validator: validateAcceptance }]}
        >
          <Checkbox className={styles.Checkbox}>
            <Trans
              i18nKey="pages.ticketSummary.form.valuablesAcceptance"
              components={tosComponents}
            />
          </Checkbox>
        </Form.Item>
      </Form>
    </>
  );
};

export default ContactForm;
